import { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import categoryApi from '_api/category'
import { getCategoryListAction } from '_redux/modules/category'
import { unassignProductsCategoryAction } from '_redux/modules/product'

export default function useCategoryEditDelete() {
  const dispatch = useDispatch()
  const { addToast } = useToasts()

  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [isDeletingId, setIsDeletingId] = useState(null)

  const handleEditClick = useCallback((category) => {
    setEditingId(category.id)
    setEditingName(category.name || '')
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingId(null)
    setEditingName('')
  }, [])

  const handleSaveEdit = useCallback(
    async (categoryId) => {
      if (!editingName.trim()) {
        addToast('Category name cannot be empty', { appearance: 'warning', autoDismiss: true })
        return
      }

      try {
        setIsSavingEdit(true)
        const data = {
          categoryId,
          name: editingName.trim(),
        }
        const response = await categoryApi.editCategory(data)
        addToast(response?.msgResp?.message || 'Category updated successfully', {
          appearance: 'success',
          autoDismiss: true,
        })
        setEditingId(null)
        setEditingName('')
        await dispatch(getCategoryListAction(true))
      } catch (error) {
        addToast(error?.msgResp || 'Failed to update category', { appearance: 'error', autoDismiss: true })
      } finally {
        setIsSavingEdit(false)
      }
    },
    [editingName, addToast, dispatch]
  )

  const handleDeleteCategory = useCallback(
    async (categoryId) => {
      if (!window.confirm('Are you sure you want to delete this category?')) return

      try {
        setIsDeletingId(categoryId)
        const response = await categoryApi.deleteCategory(categoryId)
        addToast(
          response?.msgResp?.message || response?.message || 'Category deleted successfully',
          { appearance: 'success', autoDismiss: true }
        )

        const resData = response?.data || response?.msgResp || response || {}
        const unassignedProductCount =
          resData?.unassignedProductCount ?? response?.unassignedProductCount ?? 0
        const productIds = resData?.productIds || response?.productIds || []

        if (unassignedProductCount > 0 && Array.isArray(productIds) && productIds.length > 0) {
          dispatch(unassignProductsCategoryAction(productIds))
        }

        await dispatch(getCategoryListAction(true))
      } catch (error) {
        addToast(error?.msgResp || 'Failed to delete category', { appearance: 'error', autoDismiss: true })
      } finally {
        setIsDeletingId(null)
      }
    },
    [addToast, dispatch]
  )

  const resetEditState = useCallback(() => {
    setEditingId(null)
    setEditingName('')
    setIsSavingEdit(false)
    setIsDeletingId(null)
  }, [])

  return {
    editingId,
    editingName,
    setEditingName,
    isSavingEdit,
    isDeletingId,
    handleEditClick,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteCategory,
    resetEditState,
  }
}
